import { useState, useEffect, useMemo } from "react"
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import { getProfile } from "~/api/auth"
import { ASSESSMENT_QUESTIONS } from "~/data/assessmentQuestions"
import { DOG_AGE_RANGE_OPTIONS, type DogAgeRange } from "~/data/dogAgeRanges"
import { createAssessmentSession, updateAssessmentSession, createDevShortcutSession } from "~/api/assessmentSessions"
import type { LayoutOutletContext } from "~/components/Layout"
import { PageShell } from "~/components/PageShell"
import { isDev } from "~/lib/isDev"
import { shuffleCopy } from "~/lib/shuffle"

type Step = "name" | "questions" | "submitting"
type DogSex = "male" | "female"

function shuffledAssessmentQuestionIds(): number[] {
  return shuffleCopy(ASSESSMENT_QUESTIONS.map((q) => q.id))
}

export function AssessmentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, authLoading } = useOutletContext<LayoutOutletContext>() ?? {
    profile: null,
    authLoading: true,
  }
  const [step, setStep] = useState<Step>("name")
  const [dogName, setDogName] = useState("")
  const [dogAgeRange, setDogAgeRange] = useState<DogAgeRange | "">("")
  const [dogSex, setDogSex] = useState<DogSex | null>(null)
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [shuffledQuestionIds, setShuffledQuestionIds] = useState<number[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const devType = isDev() ? searchParams.get("type") : null
  const isRetake = searchParams.get("retake") === "1" && !devType
  const [retakeBootstrapping, setRetakeBootstrapping] = useState(isRetake)

  useEffect(() => {
    if (!devType) return
    createDevShortcutSession(devType, searchParams.get("name") ?? undefined)
      .then((result) => {
        navigate("/assessment/results", {
          state: { sessionToken: result.session_token, result },
          replace: true,
        })
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Dev shortcut failed")
      })
  }, [devType, searchParams, navigate])

  useEffect(() => {
    if (devType) return
    if (!isRetake) {
      setRetakeBootstrapping(false)
      return
    }
    let cancelled = false
    setRetakeBootstrapping(true)
    getProfile()
      .then((profile) => {
        if (cancelled) return
        const dog = profile.dog_profile
        if (!dog?.name?.trim() || !dog.sex || !dog.age_range) {
          setRetakeBootstrapping(false)
          return
        }
        return createAssessmentSession(dog.name.trim(), dog.sex, dog.age_range)
      })
      .then((session) => {
        if (cancelled || !session) return
        setDogName(session.dog_name)
        setDogAgeRange(session.age_range)
        setDogSex(session.sex)
        setSessionToken(session.session_token)
        setShuffledQuestionIds(shuffledAssessmentQuestionIds())
        setCurrentQuestionIndex(0)
        setStep("questions")
        setRetakeBootstrapping(false)
      })
      .catch(() => {
        if (!cancelled) setRetakeBootstrapping(false)
      })
    return () => {
      cancelled = true
    }
  }, [isRetake, devType])

  const questionCount = ASSESSMENT_QUESTIONS.length
  const currentQuestionId = shuffledQuestionIds?.[currentQuestionIndex]
  const question =
    currentQuestionId != null
      ? ASSESSMENT_QUESTIONS.find((q) => q.id === currentQuestionId)
      : undefined
  const shuffledOptions = useMemo(
    () => (question ? shuffleCopy(question.options) : []),
    [question?.id],
  )

  if (devType) {
    return (
      <PageShell>
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        )}
      </PageShell>
    )
  }

  if (retakeBootstrapping) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  const isLastQuestion =
    shuffledQuestionIds != null && currentQuestionIndex === shuffledQuestionIds.length - 1
  const progress =
    step === "questions"
      ? ((currentQuestionIndex + 1) / questionCount) * 100
      : 0

  async function handleSubmitName(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = dogName.trim()
    if (!trimmed || !dogSex || !dogAgeRange) return
    try {
      const session = await createAssessmentSession(trimmed, dogSex, dogAgeRange)
      setSessionToken(session.session_token)
      setShuffledQuestionIds(shuffledAssessmentQuestionIds())
      setCurrentQuestionIndex(0)
      setStep("questions")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start assessment")
    }
  }

  const trimmedDogName = dogName.trim()
  const canStartAssessment = Boolean(trimmedDogName && dogAgeRange && dogSex)
  const startButtonLabel = trimmedDogName ? `Start ${trimmedDogName}'s Assessment` : "Start Assessment"

  function handleAnswer(letter: string) {
    if (!question) return
    const next = { ...answers, [String(question.id)]: letter }
    setAnswers(next)
    if (isLastQuestion) {
      setStep("submitting")
      submitAndFinish(next)
    } else {
      setCurrentQuestionIndex((i) => i + 1)
    }
  }

  async function submitAndFinish(finalAnswers: Record<string, string>) {
    if (!sessionToken) return
    setError(null)
    try {
      const result = await updateAssessmentSession(sessionToken, {
        answers: finalAnswers,
        completed: true,
      })
      navigate("/assessment/results", { state: { sessionToken, result } })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not submit")
      setStep("questions")
    }
  }

  if (step === "name") {
    return (
      <PageShell innerClassName="max-w-lg w-full">
        <section className="w-full rounded-lg bg-[var(--color-surface-card)] p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            What&apos;s your dog&apos;s name?
          </h1>
          <form onSubmit={handleSubmitName} className="mt-6 flex flex-col gap-4">
            <input
              type="text"
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              placeholder="Enter your dog's name to begin"
              className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
              autoFocus
            />
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-[var(--color-text-primary)]">Dog Age Range</span>
              <select
                value={dogAgeRange}
                onChange={(e) => setDogAgeRange(e.target.value as DogAgeRange | "")}
                className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)] focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
              >
                <option value="" disabled>
                  Select age range
                </option>
                {DOG_AGE_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setDogSex("male")}
                aria-pressed={dogSex === "male"}
                className={`min-w-32 rounded-lg border px-6 py-2 text-sm font-medium transition ${
                  dogSex === "male"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Male
              </button>
              <button
                type="button"
                onClick={() => setDogSex("female")}
                aria-pressed={dogSex === "female"}
                className={`min-w-32 rounded-lg border px-6 py-2 text-sm font-medium transition ${
                  dogSex === "female"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Female
              </button>
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={!canStartAssessment}
              className={`rounded-lg px-6 py-3 font-medium transition ${
                canStartAssessment
                  ? "bg-[var(--color-text-primary)] text-[var(--color-surface-card)] hover:opacity-90"
                  : "cursor-not-allowed bg-[var(--color-divider)] text-[var(--color-text-secondary)]"
              }`}
            >
              {startButtonLabel}
            </button>
            <p className="text-center text-sm text-[var(--color-text-secondary)]">
              We&apos;ll use this to personalize tips for your dog.
            </p>
            {!authLoading && !profile && (
              <p className="text-center text-sm text-[var(--color-text-secondary)]">
                Already have an account?{" "}
                <Link to="/login" className="font-medium text-[var(--color-text-primary)] underline">
                  Log in
                </Link>
              </p>
            )}
          </form>
        </section>
      </PageShell>
    )
  }

  if (step === "submitting") {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Submitting...</p>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full">
      <div className="w-full">
        <div className="mb-6 mt-4 h-2 overflow-hidden rounded-full bg-[var(--color-divider)]/50">
          <div
            className="h-full rounded-full bg-[var(--color-text-primary)] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mb-2 text-sm text-[var(--color-text-secondary)]">
          Question {currentQuestionIndex + 1} of {questionCount}
        </p>
        <h2 className="mb-6 text-xl font-semibold text-[var(--color-text-primary)]">
          {question?.question}
        </h2>
        <ul className="flex flex-col gap-3">
          {shuffledOptions.map((opt) => (
            <li key={opt.letter}>
              <button
                type="button"
                onClick={() => handleAnswer(opt.letter)}
                className="w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-3 text-left text-[var(--color-text-primary)] transition hover:border-[var(--color-text-primary)] hover:bg-[var(--color-surface-card)]/80 focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
        {currentQuestionIndex > 0 && (
          <button
            type="button"
            onClick={() => setCurrentQuestionIndex((i) => i - 1)}
            className="mt-6 text-sm text-[var(--color-text-primary)] underline"
          >
            Back
          </button>
        )}
      </div>
    </PageShell>
  )
}
